/**
 * Emoji Picker Component
 * Displays a popover with emoji options for reactions
 */

import { useState, useRef, useEffect } from 'react';
import { handleTablistArrowKeys } from '@/utils/a11y';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  onClose: () => void;
  placement?: 'left' | 'right';
}

// Common emojis for reactions
const EMOJI_CATEGORIES = {
  'Смайлици': ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘'],
  'Жестове': ['👍', '👎', '👏', '🙌', '👌', '🤝', '🤞', '✌️', '🤟', '🤘', '👊', '✊', '🤛', '🤜', '🙏'],
  'Емоции': ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘'],
  'Символи': ['✅', '❌', '⭐', '🌟', '💯', '🔥', '⚡', '💥', '✨', '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🎯'],
};

const EmojiPicker = ({ onEmojiSelect, onClose, placement = 'left' }: EmojiPickerProps) => {
  const [selectedCategory, setSelectedCategory] = useState(Object.keys(EMOJI_CATEGORIES)[0]);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleEmojiClick = (emoji: string) => {
    onEmojiSelect(emoji);
    onClose();
  };

  return (
    <div
      ref={pickerRef}
      className={`absolute bottom-full mb-2 bg-white dark:bg-dark-bg-elevated rounded-lg shadow-xl border border-gray-200 dark:border-dark-border-primary z-50 w-80 ${
        placement === 'right' ? 'right-0' : 'left-0'
      }`}
    >
      {/* Header */}
      <div className="p-3 border-b border-gray-200 dark:border-dark-border-primary">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text-primary">Избери емоджи</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded transition-colors"
            aria-label="Затвори"
          >
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Category tabs */}
      <div role="tablist" aria-label="Категории емоджи" onKeyDown={handleTablistArrowKeys} className="flex border-b border-gray-200 dark:border-dark-border-primary overflow-x-auto">
        {Object.keys(EMOJI_CATEGORIES).map((category) => (
          <button
            key={category}
            role="tab"
            id={`emoji-tab-${category}`}
            aria-selected={selectedCategory === category}
            aria-controls="emoji-panel"
            tabIndex={selectedCategory === category ? 0 : -1}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
              selectedCategory === category
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary'
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Emoji grid */}
      <div
        className="p-3 max-h-64 overflow-y-auto"
        role="tabpanel"
        id="emoji-panel"
        aria-labelledby={`emoji-tab-${selectedCategory}`}
      >
        <div className="grid grid-cols-8 gap-1" role="listbox" aria-label="Емоджи">
          {EMOJI_CATEGORIES[selectedCategory as keyof typeof EMOJI_CATEGORIES].map((emoji) => (
            <button
              key={emoji}
              role="option"
              aria-selected={false}
              aria-label={emoji}
              onClick={() => handleEmojiClick(emoji)}
              className="w-10 h-10 flex items-center justify-center text-2xl hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded transition-colors"
              title={emoji}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmojiPicker;
